import {IonAlert} from "@ionic/react";
import {useEffect, useState} from "react";
import _ from "./index";

// 後端無法連線 (網路錯誤 / 5xx，例如 nginx 502/504) 時，
// 顯示「服務錯誤」alert，按「重試」後重新送出同一個 request。
// 所有 store 都用全域 fetch() 呼叫 `${API_ROOT_URL}/api/...`，所以在這裡統一攔截。

let waitingRetries: Array<() => void> = [];
let setAlertOpen: ((open: boolean) => void) | null = null;

const waitForRetry = () => new Promise<void>(resolve => {
    waitingRetries.push(resolve);
    if (setAlertOpen) {
        setAlertOpen(true);
    } else {
        // alert 尚未 mount (理論上不會發生)
        window.setTimeout(() => {
            window.confirm('服務錯誤：無法連線到伺服器，按確定重試。');
            retryAll();
        }, 0);
    }
});

const retryAll = () => {
    const pending = waitingRetries;
    waitingRetries = [];
    pending.forEach(resolve => resolve());
}

const isApiRequest = (input: RequestInfo | URL) => {
    const url = typeof input === 'string' ? input : (input instanceof URL ? input.href : input.url);
    const apiRoot = `${_._s.API_ROOT_URL}/api/`;
    return url.startsWith(apiRoot) || url.startsWith('/api/');
}

let installed = false;

export const installServiceErrorGuard = () => {
    if (installed) {
        return;
    }
    installed = true;

    const originalFetch = window.fetch.bind(window);

    const guardedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        // Request 物件的 body 只能讀一次，重試時需使用 clone
        const attempt = () => originalFetch(input instanceof Request ? input.clone() : input, init);

        for (; ;) {
            try {
                const response = await attempt();
                if (response.status < 500) {
                    return response;
                }
                console.warn('[ServiceErrorGuard] HTTP', response.status, input);
            } catch (err: any) {
                if (err?.name === 'AbortError') {
                    throw err; // 呼叫端自己取消，不要重試
                }
                console.warn('[ServiceErrorGuard] network error', err, input);
            }
            await waitForRetry();
        }
    }

    window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
        return isApiRequest(input) ? guardedFetch(input, init) : originalFetch(input, init);
    }) as typeof window.fetch;
}

export const ServiceErrorAlert = () => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setAlertOpen = setIsOpen;
        if (waitingRetries.length) {
            setIsOpen(true);
        }
        return () => {
            setAlertOpen = null;
        };
    }, []);

    return <IonAlert
        isOpen={isOpen}
        backdropDismiss={false}
        keyboardClose={false}
        onDidDismiss={() => {
            setIsOpen(false);
            // 重試時又立即失敗 → 等關閉動畫結束後再顯示
            if (waitingRetries.length) {
                window.setTimeout(() => setIsOpen(true), 0);
            }
        }}
        header="服務錯誤"
        message="目前無法連線到伺服器，請確認網路連線後再重試。"
        buttons={[{
            text: '重試',
            role: 'confirm',
            handler: () => {
                retryAll();
            },
        }]}
    />;
}
